import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import FormWrapper from '~FormWrapper';
import GoodsTransportsForm from './GoodsTransportsForm';

import {
  getUserOrdererOrganizationId,
  getUserOrdererOrganizationPhone,
} from '~features/user/ducks';

import {
  getTransportUnits,
  getTransportTypes,
  getTimeInfo,
} from '../../services/api.helpers';
import { useOnSubmit } from '~hooks/useOnSubmit';
import { useOnEdit } from '~hooks/useOnEdit';
import { isDateTimeInThePast, removeEmptyKeys } from '../validations';

const validate = values => {
  const errors = {};

  if (values.packageType.length < 1) {
    errors.packageType = 'Vaadittu';
  }

  if (values.packageType.length > 0) {
    const packageTypeErrors = {};
    values.packageType.forEach((packageType, index) => {
      if (!packageType.id) {
        packageTypeErrors[index] = 'Vaadittu';
      }
      if (!packageType.itemCount) {
        packageTypeErrors[index] = 'Vaadittu';
      }
    });
    if (Object.keys(packageTypeErrors).length > 0) {
      errors.packageType = packageTypeErrors;
    }
  }

  if (values.isFromHVA && !values.fromSpace) {
    errors.fromSpace = 'Vaadittu';
  }

  if (values.isToHVA && !values.toSpace) {
    errors.toSpace = 'Vaadittu';
  }

  if (!values.isFromHVA && !values.fromAddress) {
    errors.fromAddress = 'Vaadittu';
  }

  if (!values.isToHVA && !values.toAddress) {
    errors.toAddress = 'Vaadittu';
  }

  if (!values.payerOrganization) {
    errors.payerOrganization = 'Vaadittu';
  }

  if (!values.additionalInfo) {
    errors.additionalInfo = 'Vaadittu';
  }

  if (values.deliveryTime.scheduleType === 'MAARATTY') {
    if (isDateTimeInThePast(values.deliveryTime.earliestTime)) {
      errors.deliveryTime = 'Aika ei saa olla menneisyydessä';
    } else if (
      values.deliveryTime.latestTime.isBefore(values.deliveryTime.earliestTime)
    ) {
      errors.deliveryTime = `'Viimeistään' ajan täytyy olla myöhempi ajankohta kuin 'Aikaisintaan'`;
    }
  }

  return removeEmptyKeys(errors);
};

const GoodsTransports = props => {
  const { formId, edit } = props;
  const { orderId } = useParams();
  const [timeInfo, setTimeInfo] = useState();
  const [transportUnits, setTransportUnits] = useState([]);
  const [transportTypes, setTransportTypes] = useState([]);
  const organizations = useSelector(state => state.reducer.organizations);
  const userPhone = useSelector(state =>
    getUserOrdererOrganizationPhone(state)
  );
  const userOrganizationId = useSelector(state =>
    getUserOrdererOrganizationId(state)
  );

  const userOrganization = organizations?.find(
    o => o.id === userOrganizationId
  );

  useEffect(() => {
    const update = async () => {
      const tempTimeInfo = await getTimeInfo(formId);
      const units = await getTransportUnits();
      const types = await getTransportTypes();
      setTransportUnits(
        units?.map(u => ({ value: u.id, label: u.description }))
      );
      setTransportTypes(types);
      setTimeInfo(tempTimeInfo);
    };
    update();
  }, [formId]);

  const onFormSubmit = useOnSubmit();
  const onFormEdit = useOnEdit();

  return (
    <Formik
      initialValues={{
        packageType: [{ id: null, itemCount: '1' }],
        isToHVA: true,
        isFromHVA: true,
        fromSpace: null,
        toSpace: null,
        fromAddress: '',
        toAddress: '',
        deliveryTime: {
          scheduleType: 'NORMAALI',
          earliestTime: null,
          latestTime: null,
        },
        additionalInfo: '',
        contactPhone: userPhone,
        infectionRisk: false,
        payerOrganization: userOrganization
          ? {
              value: userOrganization.id,
              label: userOrganization.name,
            }
          : null,
        function: '',
        project: '',
      }}
      validate={validate}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        const method = edit ? onFormEdit : onFormSubmit;
        await method({
          fields: {
            ...values,
            packageType: values.packageType?.length
              ? values.packageType.map(p => ({
                  id: p.id.value,
                  itemCount: p.itemCount,
                }))
              : null,
            payerOrganization: values.payerOrganization
              ? values.payerOrganization.value
              : null,
            scheduleType: values.deliveryTime.scheduleType,
            earliestTime: values.deliveryTime.earliestTime,
            latestTime: values.deliveryTime.latestTime,
            fromSpaceType: values.isFromHVA ? 'HVA_ORGANIZATION' : 'OTHER',
            toSpaceType: values.isToHVA ? 'HVA_ORGANIZATION' : 'OTHER',

            // Clean up these if we select a HVA_ORGANIZATION
            fromAddress: !values.isFromHVA ? values.fromAddress : undefined,
            toAddress: !values.isToHVA ? values.toAddress : undefined,

            // Clean up these if we select OTHER address
            fromSpace: values.isFromHVA ? values.fromSpace : undefined,
            toSpace: values.isToHVA ? values.toSpace : undefined,
          },
          setSubmitting,
          orderId,
          formId,
        });
      }}
    >
      {formikProps => (
        <FormWrapper
          {...props}
          title="Tilaa tavarakuljetus"
          organizations={organizations}
          userOrganization={userOrganization}
          timeInfo={timeInfo}
          transportTypes={transportTypes}
          transportUnits={transportUnits}
          formik={formikProps}
          Component={GoodsTransportsForm}
        />
      )}
    </Formik>
  );
};

export default GoodsTransports;
